<script setup lang="ts">
import commonContent from "~/content/common.json";

const { staticUrl } = useImageUrl();
const route = useRoute();
const showJoinModal = ref(false);
const appVersion = useRuntimeConfig().public.appVersion;
const { apiVersion } = useApiVersion();

const versionTitle = computed(() => {
  if (apiVersion.value) {
    return `Site v${appVersion} · API v${apiVersion.value}`;
  }
  return `Site v${appVersion}`;
});
</script>

<template>
  <footer class="footer">
    <!-- Main grid -->
    <div class="footer__main">
      <!-- Brand column -->
      <div class="footer__brand">
        <img
          :src="staticUrl('/Images/logos/logo-white.png')"
          alt="&Dine logo"
          loading="lazy"
          class="footer__brand--logo"
          @click="navigateTo({ path: '/', query: { discount: route.query.discount } })"
        />
        <p class="footer__brand--tagline">
          London's best workplace catering, made by independent makers.
        </p>
        <DynamicButton
          color="white"
          text="Join the &amp;Dine Community"
          width="100%"
          @click-event="showJoinModal = true"
        />
        <div class="footer__brand--socials">
          <a
            href="https://www.facebook.com/anddinecooking"
            target="_blank"
            rel="noopener"
            aria-label="Facebook"
          >
            <fa :icon="['fab', 'facebook']" />
          </a>
          <a
            href="https://www.instagram.com/anddinecooking"
            target="_blank"
            rel="noopener"
            aria-label="Instagram"
          >
            <fa :icon="['fab', 'instagram']" />
          </a>
          <a
            href="https://twitter.com/anddinecooking"
            target="_blank"
            rel="noopener"
            aria-label="Twitter"
          >
            <fa :icon="['fab', 'twitter']" />
          </a>
        </div>
      </div>

      <!-- Services column -->
      <div class="footer__links">
        <h4 class="footer__links--heading">Services</h4>
        <a
          href="https://landing.anddine.co.uk/our-locations-london"
          target="_blank"
        >
          Our Locations
        </a>
        <a
          href="https://landing.anddine.co.uk/premium"
          target="_blank"
        >
          &amp;Dine Premium
        </a>
        <a
          href="https://landing.anddine.co.uk/events"
          target="_blank"
        >
          &amp;Dine Events
        </a>
        <NuxtLink to="/search">Order &amp;Dine</NuxtLink>
        <!-- The only crawlable link to /sign-up on the whole site. Its one other
             entry point is a `navigateTo` button inside LoginForm.vue, which is JS,
             on a noindexed page — so without this the page was orphaned and Google
             could only reach it via the sitemap. That is half of why it lost
             "anddine sign up" to /organisation-sign-up (Notion 1156). -->
        <NuxtLink to="/sign-up">Create an Account</NuxtLink>
      </div>

      <!-- Company column -->
      <div class="footer__links">
        <h4 class="footer__links--heading">Company</h4>
        <NuxtLink to="/about">About Us</NuxtLink>
        <NuxtLink to="/blog">Blog</NuxtLink>
        <NuxtLink to="/join">Become a Maker</NuxtLink>
        <NuxtLink to="/contact">Contact Us</NuxtLink>
        <a href="mailto:hello@anddine.co.uk">Email Us</a>
      </div>

      <!-- Legal column -->
      <div class="footer__links">
        <h4 class="footer__links--heading">Legal</h4>
        <NuxtLink to="/privacy-policy">Privacy Policy</NuxtLink>
        <NuxtLink to="/website-terms">Website Terms</NuxtLink>
        <NuxtLink to="/customer-terms">Customer Terms</NuxtLink>
        <NuxtLink to="/modern-slavery">Modern Slavery</NuxtLink>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="footer__bottom">
      <span>
        <fa :icon="['fal', 'copyright']" />
        &amp;Dine {{ new Date().getFullYear() }}. All rights reserved.
      </span>
      <span
        class="footer__bottom--version"
        :title="versionTitle"
      >
        v{{ appVersion }}<template v-if="apiVersion"> · api v{{ apiVersion }}</template>
      </span>
    </div>

    <!-- HubSpot Modal -->
    <DynamicModal
      v-if="showJoinModal"
      @close-modal="showJoinModal = false"
    >
      <template #body>
        <HubspotForm
          portal-id="139704907"
          form-id="51a59bd9-d01d-4962-be63-4cd40f008409"
          region="eu1"
        />
      </template>
    </DynamicModal>
  </footer>
</template>

<style lang="scss">
.footer {
  background-color: var(--color-pink-1);
  padding: 4rem 6% 0;
  position: relative;
  z-index: 3333;
  @media (max-width: $tablet) {
    padding: 3rem 5% 0;
  }

  // ---------- Main grid ----------
  &__main {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
    @media (max-width: $laptop) {
      grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 2rem;
    }
    @media (max-width: $tablet) {
      grid-template-columns: 1fr 1fr;
      gap: 2.5rem;
    }
    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }

  // ---------- Brand column ----------
  &__brand {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    @media (max-width: $tablet) {
      grid-column: 1 / -1;
    }
    &--logo {
      width: 5rem;
      cursor: pointer;
      transition: transform 0.2s ease;
      &:hover {
        transform: scale(1.05);
      }
    }
    &--tagline {
      color: rgba(255, 255, 255, 0.85);
      font-size: 0.95rem;
      line-height: 1.6;
      max-width: 22rem;
    }
    &--socials {
      display: flex;
      gap: 1rem;
      margin-top: 0.25rem;
      a {
        color: var(--color-white-1);
        font-size: 1.25rem;
        transition:
          transform 0.2s ease,
          opacity 0.2s ease;
        cursor: pointer;
        &:hover {
          transform: scale(1.15);
          opacity: 0.8;
        }
      }
    }
  }

  // ---------- Link columns ----------
  &__links {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    &--heading {
      color: var(--color-white-1);
      font-size: 1rem;
      margin: 0 0 0.25rem;
      letter-spacing: 0.03em;
    }
    a {
      // Footer links: full white (100%) idle, 70% on hover per the
      // style-guide rule (was 80% → 100%).
      color: var(--color-white-1) !important;
      text-decoration: none !important;
      font-size: 0.9rem;
      line-height: 1.4;
      cursor: pointer;
      transition:
        color 0.2s ease,
        padding-left 0.2s ease;
      &:hover {
        color: rgba(255, 255, 255, 0.7) !important;
        padding-left: 4px;
      }
    }
  }

  // ---------- Bottom bar ----------
  &__bottom {
    margin-top: 3rem;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    padding: 1.25rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    @media (max-width: $tablet) {
      margin-top: 2rem;
    }
    > span {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.85rem;
      svg {
        margin-right: 0.25rem;
      }
    }
    &--version {
      color: rgba(255, 255, 255, 0.7);
      flex-shrink: 0;
      font-size: 0.7rem;
      line-height: 1.2;
      opacity: 0.7;
      white-space: nowrap;
    }
  }
}
</style>
